// AdjustStockModal.tsx
import React, { useEffect, useState } from "react";
import styles from "./AdjustStockModal.module.css";
import { FaTimes, FaPlus, FaMinus } from "react-icons/fa";
import WebService from "../Services/WebService";
import { mediaUrl } from "../Services/mediaUrl";
import { toast } from "react-toastify";
import axios from "axios";
import environment from "../environments/environment";
import Swal from "sweetalert2";

interface RawMaterialInfo {
  id: number;
  name: string;
  sku_id: string;
  images: { file_name: string }[];
  quantity: number;
}
interface ProductInfoType {
  id: number;
  name: string;
  sku_id: string;
  images: { file_name: string }[];
  quantity: number;
}
interface ReasonOption { id: number; label: string; }
interface AdjustStockModalProps {
  open: boolean;
  onClose: () => void;
  rawMaterial?: RawMaterialInfo;
  product?: ProductInfoType;
  mode?: "raw-material" | "product";
  onSuccess?: () => void;
}

const AdjustStockModal: React.FC<AdjustStockModalProps> = ({
  open,
  onClose,
  rawMaterial,
  product,
  mode = "raw-material",
  onSuccess,
}) => {
  const item = mode === "product" ? product : rawMaterial;
  const currentQuantity = item?.quantity ?? 0;
  const [operation, setOperation] = useState<"add" | "remove" | "">("");
  const [qty, setQty] = useState<string>("");
  const [reasonId, setReasonId] = useState<number | "">("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [reasons, setReasons] = useState<ReasonOption[]>([]);
  const [reasonsLoading, setReasonsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQty("");
    setOperation("");
    setReasonId("");
    setNote("");
    setError("");
    setReasons([]);
    setReasonsLoading(true);
    WebService.getAPI<{ data: { id: number; reason: string }[] }>({
      action: "/v1/door-management/adjustment/reasons",
    })
      .then((res) => {
        const arr = Array.isArray(res.data) ? res.data : [];
        setReasons(arr.map((r: { id: number; reason: string }) => ({
          id: r.id,
          label: r.reason,
        })));
      })
      .finally(() => setReasonsLoading(false));
  }, [open]);

  if (!open || !item) return null;

  const parsedQty = qty === "" ? 0 : Math.max(0, Number(qty.replace(/[^0-9]/g, "")));

  // Calculate net quantity (new quantity after adjustment)
  let netQuantity = currentQuantity;
  if (operation === "add") {
    netQuantity = currentQuantity + parsedQty;
  } else if (operation === "remove") {
    netQuantity = currentQuantity - parsedQty;
  }

  const handleQtyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/[^0-9]/g, "");
    if (val.length > 5) val = val.slice(0, 5);
    setQty(val);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!operation) {
      setError("Please select whether you want to Add or Remove stock.");
      toast.error("Please select Add or Remove.");
      return;
    }
    if (!qty || parsedQty === 0) {
      setError("Please enter a valid quantity.");
      toast.error("Please enter a valid quantity.");
      return;
    }
    if (operation === "remove" && !reasonId) {
      setError("Please select a reason for adjustment.");
      toast.error("Please select a reason for adjustment.");
      return;
    }
    if (operation === "remove" && parsedQty > currentQuantity) {
      setError("You cannot remove more than current stock!");
      toast.error("You cannot remove more than current stock!");
      return;
    }

    // SweetAlert confirmation
    const result = await Swal.fire({
      title: "Confirm Stock Adjustment",
      html: `
        <div style="font-size:16px;">
          <b>Operation:</b> ${operation === "add" ? "Add" : "Remove"}<br/>
          <b>Current Quantity:</b> ${currentQuantity}<br/>
          <b>${operation === "add" ? "Add" : "Remove"} Quantity:</b> ${parsedQty}<br/>
          <b>Net Quantity:</b> ${netQuantity}
        </div>
        <br/>
        <div style="color:#555;font-size:15px;">
          Are you sure you want to proceed?
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#FFD400",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Yes, Adjust Stock",
      cancelButtonText: "Cancel",
      heightAuto: false,
      focusCancel: true,
    });
    if (!result.isConfirmed) return;
    setLoading(true);
    try {
      const formData = new FormData();
      const adjusted_stock = operation === "add" ? parsedQty : -parsedQty;
      formData.append("adjusted_stock", adjusted_stock.toString());
      if (operation === "remove" && reasonId) formData.append("reason_id", String(reasonId));
      if (note) formData.append("note", note);

      const endpointType = mode === "product" ? "product" : "raw-material";
      const url = `${environment.Base_URL}/v1/door-management/${endpointType}/adjust-stock/${item.id}`;
      let token = localStorage.getItem("token") || sessionStorage.getItem("token") || "";
      token = token.trim();
      if (token && !token.toLowerCase().startsWith("bearer ")) {
        token = `Bearer ${token}`;
      }
      await axios.put(url, formData, { headers: { Authorization: token } });

      setLoading(false);
      toast.success("Stock adjusted successfully!");
      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      setLoading(false);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to adjust stock";
      setError(msg);
      // toast.error(msg);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalBox}>
        <button className={styles.closeBtn} onClick={onClose} title="Close">
          <FaTimes />
        </button>
        <form onSubmit={handleSave}>
          <div className={styles.header}>
            Adjust Stock {mode === "product" ? "for Product" : "for Raw Material"}
          </div>
          <div className={styles.materialCard}>
            <img
              src={
                item.images?.[0]?.file_name
                  ? mediaUrl(item.images[0].file_name)
                  : "https://placehold.co/60x60?text=No+Image"
              }
              alt={item.name}
              className={styles.materialImg}
            />
            <div className={styles.materialInfo}>
              <div className={styles.materialName}>{item.name}</div>
              <div className={styles.materialMeta}>
                <span>Current Quantity: {currentQuantity}</span>
                <span>SKU: {item.sku_id}</span>
              </div>
            </div>
          </div>
          <div className={styles.inputGroup}>
            <label>Operation</label>
            <div style={{ display: "flex", gap: 18, marginBottom: 9 }}>
              <button
                type="button"
                className={`${styles.qBtn} ${operation === "add" ? styles.activeType : ""}`}
                onClick={() => setOperation("add")}
                tabIndex={-1}
              >
                <FaPlus style={{ marginBottom: -2 }} /> Add
              </button>
              <button
                type="button"
                className={`${styles.qBtn} ${operation === "remove" ? styles.activeType : ""}`}
                onClick={() => setOperation("remove")}
                tabIndex={-1}
              >
                <FaMinus style={{ marginBottom: -2 }} /> Remove
              </button>
            </div>
          </div>
          <div className={styles.inputGroup} style={{ alignItems: "center", flexDirection: "row", gap: 14 }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <label>
                {operation === "add"
                  ? "Add Quantity"
                  : operation === "remove"
                  ? "Remove Quantity"
                  : "Quantity"}
              </label>
              <input
                type="number"
                className={styles.quantityInput}
                placeholder={operation === "add" ? " Quantity" : operation === "remove" ? " Quantity" : "Select operation first"}
                value={qty}
                onChange={handleQtyChange}
                disabled={!operation || loading}
                min={1}
                max={999999}
                style={{ width: 110, fontWeight: 600, fontSize: 17, textAlign: "center" }}
              />
            </div>
            {/* Net Quantity inline at the end of the row */}
            <div style={{
              minWidth: 115,
              padding: "8px 12px",
              borderRadius: 7,
              background: "#f6fbee",
              border: "1.1px solid #e3e7f0",
              fontWeight: 700,
              fontSize: 16,
              color: netQuantity < 0 ? "#e43e2d" : "#15803d",
              textAlign: "center"
            }}>
              Net Quantity: {netQuantity}
            </div>
          </div>
          {operation !== "add" && (
          <div className={styles.inputGroup}>
            <label>Reason for Adjustment</label>
            {reasonsLoading ? (
              <div style={{ color: "#FFD400", padding: 7 }}>
                Loading reasons...
              </div>
            ) : (
              <select
                className={styles.selectInput}
                value={reasonId}
                onChange={e => setReasonId(Number(e.target.value))}
                required
              >
                <option value="">Select reason</option>
                {reasons.map(r => (
                  <option value={r.id} key={r.id}>{r.label}</option>
                ))}
              </select>
            )}
          </div>
          )}
          <div className={styles.inputGroup}>
            <label>Additional Notes</label>
            <textarea
              className={styles.textarea}
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Add notes (optional)"
              rows={2}
            />
          </div>
          {error && <div className={styles.errorMsg}>{error}</div>}
          <div className={styles.actionRow}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button className={styles.saveBtn} type="submit" disabled={loading}>
              {loading ? "Saving..." : "Save Adjustment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdjustStockModal;
